<template>
    <div :class="{'chatbot-wrapper': true, 'shadow-md': isChatOpen}">
      <div v-if="!isChatOpen" class="icon-wrapper" @click="toggleChat">
        <img :src="cyberIcon" :alt="$t('ChatBot.CyberAdvisor')" :width="30" :height="30" />
      </div>
      <div v-if="isChatOpen" ref="dialogEl" class="chat-box" :class="{ expanded: expanded }" >
        <div class="chat-header">
          <span>{{ $t('ChatBot.CyberAdvisor') }}</span>
          <div class="flex gap-5 items-center">
            <CGButton variant="ghost" :aria-label="$t('Wcag.' + (expanded ? 'Compress' : 'Expand') + 'Modal')" @click="toggleExpanded">
              <Icon :name="expanded ? 'compress' : 'expand'" class="text-white"/>
            </CGButton>
            <CGButton variant="ghost" :aria-label="$t('General.Close')" @click="toggleChat">
              <Icon name="close" class="text-white" />
            </CGButton>
          </div>
        </div>
        <div  class="chat-body">
          <ChatMessage ref="chatBody" :messages="messages" :is-typing="typingIndicator" />
        </div>
        <div class="py-2 pb-4">
          <ChatInputForm
              :message="newMessage"
              :image-url="imageUrl"
              :image-file="imageFile"
              :errored="chatInputError"
              :is-typing="typingIndicator"
              @send="sendMessage"
              @update:image-url="imageUrl = $event"
              @update:image-file="imageFile = $event"
              @update:message="newMessage = $event"
              @update:search="searchMessage = $event"
            >
            <template #buttons>
              <div class="pr-4">
                <CGButton :disabled="typingIndicator || !(newMessage || imageFile)" @click="sendMessage">
                  <span v-if="expanded">{{ $t('ChatBot.SendMessage') }}</span>
                  <Icon v-else name="send" />
                </CGButton>
              </div>
            </template>
          </ChatInputForm>
        </div>
      </div>
    </div>
</template>
  
<script>
  import { defineComponent } from 'vue';
  import { messageType } from '@/common/constants';

  import Icon from '../atoms/Icon.vue';
  import cyberIcon from '@/assets/icons/cyber-advisor.svg';
  import llmService from '@/services/llm.service.js';
  
  export default defineComponent({
    components: { Icon },
  
    data() {
      return {
        expanded: false,
        initialSize: undefined,
        isChatOpen: false,
        newMessage: '',
        searchMessage: false,
        conversationId: '',
        messageType: messageType,
        typingIndicator: false,
        account_name: this.$profile.firstname || this.$profile.lastname || this.$profile.username,
        language: this.$profile.language ?? this.$company.default_language,
        messages: [],
        imageUrl: '',
        imageFile: null,
        chatInputError: false,
        cyberIcon
      };
    },
    created() {
      this.conversationId = crypto.randomUUID();
    },
    mounted() {
      this.newMessage = messageType.welcome
      this.sendMessage();
    },
    methods: {
      toggleChat() {
        this.isChatOpen = !this.isChatOpen;
        this.$nextTick(() => this.$methods.scrollToBottom(this.$refs.chatBody));
      },

      toggleExpanded() {
        const expand = !this.expanded;
        if (expand) {
          this.initialSize = [this.$refs.dialogEl.clientWidth, this.$refs.dialogEl.clientHeight];
        }
        this.expanded = expand;
      },

      async sendMessage() {
        if (this.imageFile || this.newMessage.trim() !== '') {
          const typeMessage = { type: messageType.sent, text: this.newMessage, imageUrl: this.imageUrl };
          // Text sent in POST request is different from the one displayed in the chat
          const text = this.searchMessage ? messageType.search + this.newMessage : this.newMessage;
          if (this.messages.length) this.messages.push(typeMessage);
          this.newMessage = '';
          this.imageUrl = '';
          this.chatInputError = false;
          this.$methods.scrollToBottom(this.$refs.chatBody);
          this.typingIndicator = true;

          const formData = new FormData();
          formData.append('id', this.conversationId);
          formData.append('company_id', this.$company.company_id);
          // Normalize language to base code (pt_br -> pt) for LLM welcome message
          formData.append('language', this.language.toLowerCase().split('_')[0]);
          formData.append('message', text);
          formData.append('file', this.imageFile); 

          await this.$methods.invokeWithoutLoader(llmService.sendMessage, this.$t('ChatBot.SendMessageError'), formData, { headers: { 'x-account-name': this.account_name } })
            .then(response => {
              if (response?.data?.message) {
                this.messages.push({ type: messageType.received, text: response.data.message });
                this.$nextTick(() => this.$methods.scrollToBottom(this.$refs.chatBody));
              }
            })
            .catch(error => {
              console.error('Error sending message: ', error);
            })
          this.typingIndicator = false;
          this.imageFile = null;
        } else {
          this.chatInputError = true;
        };
      }, 
    },
  });
</script>
  
<style lang="scss" scoped>
  .chatbot-wrapper {
    @apply fixed bottom-5 right-5 flex items-center justify-center z-50;
  }
  
  .icon-wrapper {
    @apply h-19 w-19 flex items-center justify-center bg-white p-4 rounded-full hover:shadow-lg cursor-pointer border;
  }
  
  .chat-box {
    @apply w-80 bg-white shadow-lg rounded-lg overflow-hidden;
    
    &.expanded {
      @apply w-[92%] sm:w-[500px] fixed z-[1000];
      transform: translate(-50%, -50%);
    }
  }
  
  .chat-header {
    @apply bg-[#1f1584] text-white p-3 flex justify-between items-center;
  }
  
  .chat-body {
    @apply h-[300px] overflow-y-auto flex flex-col space-y-2 bg-gray-100;
    scrollbar-width: thin;
    scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
  
    .expanded & {
      @apply h-[450px] sm:h-[500px]
    }
  }
  
  .chat-body::-webkit-scrollbar {
    width: 6px;
  }
  
  .chat-body::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
  
</style>  